/**
 * The game's additions to core's register screen (core/auth/register-screen.ts
 * is template code and cannot be edited): a "Confirm password" field and a
 * required "I accept the Terms of Service and the Privacy Policy" checkbox
 * with links to the landing legal pages. main.ts calls `enhanceRegister`
 * right after core renders the form, and `recordTermsAcceptance` after a
 * successful sign-up (the backend stamps the current legal versions).
 *
 * Submit is checked by a capture-phase listener on the form's ancestor, so it
 * runs before core's own submit handler on the form and stops it when the
 * passwords differ or the box is not ticked. Plain DOM, no Vue; the strings
 * load on demand (a static import would pull the locale tables into the
 * entry chunk), English first, then the player's language.
 */
import { api } from '../core/network/api';

export const LEGAL_LINKS = { terms: '/legal/terms', privacy: '/legal/privacy' } as const;

interface RegisterStrings {
  confirmPassword: string;
  accept: string;
  terms: string;
  privacy: string;
  mismatch: string;
  mustAccept: string;
}

async function loadStrings(): Promise<RegisterStrings> {
  const [{ default: en }, { localeMessages }, { readLocale }] = await Promise.all([import('./i18n/locales/en'), import('./i18n'), import('./i18n/locale-pref')]);
  let mine: Partial<RegisterStrings> = {};
  try {
    mine = ((await localeMessages(readLocale())).nav as { register?: Partial<RegisterStrings> } | undefined)?.register ?? {};
  } catch {
    /* the locale chunk failed: English */
  }
  return { ...en.nav.register, ...mine };
}

/** `I accept the {terms} and the {privacy}` with the two placeholders as links (new tab, the form keeps its input). */
function fillAccept(host: HTMLElement, s: RegisterStrings) {
  host.textContent = '';
  for (const part of s.accept.split(/(\{terms\}|\{privacy\})/)) {
    if (part === '{terms}' || part === '{privacy}') {
      const key = part === '{terms}' ? 'terms' : 'privacy';
      const a = document.createElement('a');
      a.href = LEGAL_LINKS[key];
      a.target = '_blank';
      a.rel = 'noopener';
      a.className = 'underline';
      a.dataset.legalLink = key;
      a.textContent = s[key];
      host.appendChild(a);
    } else if (part) {
      host.appendChild(document.createTextNode(part));
    }
  }
}

/**
 * Adds the confirm-password field and the terms checkbox to core's register
 * form inside `root`. Returns a teardown that removes the submit guard.
 */
export function enhanceRegister(root: HTMLElement): () => void {
  const form = root.querySelector<HTMLFormElement>('#register-form');
  const pw = form?.querySelector<HTMLInputElement>('input[name="password"]');
  const pwLabel = pw?.closest('label');
  if (!form || !pw || !pwLabel || form.querySelector('[data-confirm-password]')) return () => undefined;
  const errEl = root.querySelector<HTMLElement>('#register-error');

  // Same markup and classes as core's password field, so the auth-card styling applies.
  const label = document.createElement('label');
  label.className = pwLabel.className;
  const caption = document.createElement('span');
  caption.className = pwLabel.querySelector('span')?.className ?? '';
  caption.dataset.confirmCaption = '';
  const confirm = document.createElement('input');
  confirm.type = 'password';
  confirm.name = 'passwordConfirm';
  confirm.autocomplete = 'new-password';
  confirm.required = true;
  confirm.minLength = pw.minLength > 0 ? pw.minLength : 8;
  confirm.className = pw.className;
  confirm.dataset.confirmPassword = '';
  // Dev builds prefill core's password; mirror it so the dev account flow stays one click.
  if (import.meta.env.DEV) confirm.value = pw.value;
  label.append(caption, confirm);
  pwLabel.after(label);

  const row = document.createElement('div');
  row.className = 'mb-3.5 flex min-h-11 items-start gap-3';
  row.dataset.termsRow = '';
  const box = document.createElement('input');
  box.type = 'checkbox';
  box.id = 'tkd-accept-terms';
  box.name = 'acceptTerms';
  box.required = true;
  box.className = 'mt-0.5 size-6 shrink-0 cursor-pointer accent-[var(--color-hazard)]';
  box.dataset.acceptTerms = '';
  const text = document.createElement('label');
  text.htmlFor = box.id;
  text.className = 'cursor-pointer text-[13px] leading-snug text-text-dim';
  text.dataset.termsText = '';
  row.append(box, text);
  const altcha = form.querySelector('altcha-widget')?.parentElement;
  if (altcha && altcha.parentElement === form) altcha.before(row);
  else label.after(row);

  let strings: RegisterStrings | null = null;
  const ready = loadStrings()
    .then((s) => {
      strings = s;
      caption.textContent = s.confirmPassword;
      fillAccept(text, s);
    })
    .catch(() => undefined);

  const showError = (key: 'mismatch' | 'mustAccept', field: HTMLInputElement) => {
    field.setAttribute('aria-invalid', 'true');
    if (errEl) {
      if (!errEl.id) errEl.id = 'register-error';
      field.setAttribute('aria-describedby', errEl.id);
      const put = () => {
        errEl.textContent = strings?.[key] ?? '';
        errEl.hidden = false;
      };
      put();
      if (!strings) void ready.then(put);
    }
    field.focus();
  };
  const clear = (field: HTMLInputElement) => {
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');
  };

  const onSubmit = (e: Event) => {
    if (e.target !== form) return;
    if (confirm.value !== pw.value) {
      e.preventDefault();
      e.stopPropagation();
      showError('mismatch', confirm);
      return;
    }
    clear(confirm);
    if (!box.checked) {
      e.preventDefault();
      e.stopPropagation();
      showError('mustAccept', box);
      return;
    }
    clear(box);
  };
  const onInput = () => {
    if (confirm.value === pw.value) clear(confirm);
  };
  const onChange = () => box.checked && clear(box);
  root.addEventListener('submit', onSubmit, true);
  confirm.addEventListener('input', onInput);
  pw.addEventListener('input', onInput);
  box.addEventListener('change', onChange);
  return () => {
    root.removeEventListener('submit', onSubmit, true);
    confirm.removeEventListener('input', onInput);
    pw.removeEventListener('input', onInput);
    box.removeEventListener('change', onChange);
  };
}

/**
 * Records the ticked checkbox for the player who just signed up
 * (POST /api/game/consent/terms, game-api.md). Never blocks the sign-up: a
 * failed call is logged and the player carries on.
 */
export async function recordTermsAcceptance(post: (path: string, body: unknown) => Promise<unknown> = (p, b) => api.post(p, b)): Promise<void> {
  try {
    await post('/game/consent/terms', { accepted: true });
  } catch (err) {
    console.warn('[tankodromo] terms acceptance not recorded', err);
  }
}
